import { Grid, Hidden, makeStyles, MenuItem, Select, SelectableChipSmall, Tab, Tabs, useTheme } from '@paintscout/ui'
import type { View } from 'react-big-calendar'
import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import ProductionCalendar, {
  ProductionCalendarProvider,
  useProductionCalendar
} from 'src/components/common/ProductionCalendar'

const useViewSelectStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
    borderRadius: theme.borderRadius.lg,
    minWidth: 0,
    '& .MuiSelect-select': {
      border: 'none !important',
      padding: `${theme.spacing(0.75)}px ${theme.spacing(3.75)}px ${theme.spacing(0.75)}px ${theme.spacing(1.25)}px !important`,
      minWidth: 0,
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 500,
      color: theme.paintscout.palette.brand.Neutral.P30,
      backgroundColor: 'transparent',
      minHeight: 0,
      lineHeight: 1.5,
      boxShadow: 'none !important'
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
      borderColor: 'transparent !important',
      boxShadow: 'none !important'
    },
    '& .MuiSelect-icon': {
      color: `${theme.paintscout.palette.brand.Neutral.P50} !important`,
      right: `${theme.spacing(0.75)}px !important`,
      width: `${theme.spacing(2)}px !important`,
      padding: '0 !important'
    }
  }
}))

function ProductionViewChips() {
  const theme = useTheme()
  const { view, setView, tab } = useProductionCalendar()
  const chipStyle = { backgroundColor: theme.paintscout.palette.brand.Neutral.P94 }
  return (
    <>
      {tab === 'calendar' && (
        <SelectableChipSmall
          label="Schedule"
          selected={view === 'agenda'}
          onClick={() => setView('agenda')}
          style={chipStyle}
        />
      )}
      <SelectableChipSmall label="Day" selected={view === 'day'} onClick={() => setView('day')} style={chipStyle} />
      <SelectableChipSmall label="Week" selected={view === 'week'} onClick={() => setView('week')} style={chipStyle} />
      <SelectableChipSmall
        label="Month"
        selected={view === 'month'}
        onClick={() => setView('month')}
        style={chipStyle}
      />
    </>
  )
}

function ProductionViewMobileSelect() {
  const { view, setView, tab } = useProductionCalendar()
  const classes = useViewSelectStyles()
  return (
    <Select className={classes.root} value={view} onChange={(e) => setView(e.target.value as View)}>
      {tab === 'calendar' && <MenuItem value="agenda">Schedule</MenuItem>}
      <MenuItem value="day">Day</MenuItem>
      <MenuItem value="week">Week</MenuItem>
      <MenuItem value="month">Month</MenuItem>
    </Select>
  )
}

export default function SchedulingView() {
  const [tab, setTab] = useState<'calendar' | 'map'>('calendar')
  const location = useLocation()
  const history = useHistory()

  useEffect(() => {
    // Set the tab based on the URL query parameter
    const params = new URLSearchParams(location.search)
    const tabParam = params.get('tab')
    if (tabParam === 'map' || tabParam === 'calendar') {
      setTab(tabParam)
    }
  }, [location.search])

  const handleTabChange = (_: React.ChangeEvent<any>, value: 'calendar' | 'map') => {
    setTab(value)
    history.push(`/scheduling/?tab=${value}`)
  }

  return (
    <ProductionCalendarProvider tab={tab}>
      <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
        <Grid item>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="Calendar" value="calendar" />
            <Tab label="Map" value="map" />
          </Tabs>
        </Grid>

        {tab !== 'map' && (
          <>
            <Hidden xsDown>
              <Grid item xs container justifyContent="flex-end" alignItems="center" style={{ gap: 8 }}>
                <ProductionViewChips />
              </Grid>
            </Hidden>

            <Hidden smUp>
              <Grid item>
                <ProductionViewMobileSelect />
              </Grid>
            </Hidden>
          </>
        )}

        <Grid item xs={12}>
          <ProductionCalendar />
        </Grid>
      </Grid>
    </ProductionCalendarProvider>
  )
}
